// Application root. Wires router + i18n + auth + session provider + admin shell.
//
// Routes:
//   /login                  → AuthRoutes (login, MFA challenge, new-password, MFA setup)
//   /forgot-password        → ForgotPasswordScreen
//   /reset-password         → PasswordResetScreen (?email=...)
//   /verify-email           → EmailVerifyScreen   (?email=... &token=...) [Phase 1-ready]
//   /                       → redirect to /admin/team/users
//   /admin/team/users       → UsersPage
//   /admin/team/roles       → ManageRolesPage
//
// SessionExpiredModal is mounted globally inside AuthProvider — renders
// itself (via Modal portal) only when authStatus.kind === 'session-expired'.

import type { JSX } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { PortalProvider } from '@gc-coach/design-system';
import {
  AuthProvider,
  ProtectedRoute,
  SessionExpiredModal,
  SessionProvider,
} from './auth/index.js';
import { I18nProvider } from './i18n/index.js';
import { AuthRoutes } from './routes/AuthRoutes.js';
import { EmailVerifyScreen } from './routes/auth/EmailVerifyScreen.js';
import { ForgotPasswordScreen } from './routes/auth/ForgotPasswordScreen.js';
import { PasswordResetScreen } from './routes/auth/PasswordResetScreen.js';
import { FrameworkEditorPage, FrameworksLibraryPage } from './frameworks/index.js';
import { ExcerptReviewPage, KBEditor, KBListPage, KBSearchPage } from './kb/index.js';
import { PersonaeListPage, PersonaEditor, PersonaDetail } from './personae/index.js';
import { LabsPage } from './labs/index.js';
import { SettingsPage } from './settings/index.js';
import {
  SourceDocumentDetailPage,
  MapReviewPage,
  SourceDocumentUploadPage,
  SourceDocumentsListPage,
} from './source-documents/index.js';
import { TenantsPage, TenantUsersPage } from './tenants/index.js';
import { ManageRolesPage, UsersPage } from './users/index.js';

export function App(): JSX.Element {
  return (
    <BrowserRouter>
      <PortalProvider portal="admin">
        <I18nProvider>
          <AuthProvider>
            <SessionProvider>
            <Routes>
              <Route path="/login" element={<AuthRoutes />} />
              <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
              <Route path="/reset-password" element={<PasswordResetScreen />} />
              <Route path="/verify-email" element={<EmailVerifyScreen />} />
              <Route
                path="/admin/team/users"
                element={
                  <ProtectedRoute>
                    <UsersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/team/roles"
                element={
                  <ProtectedRoute>
                    <ManageRolesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/customers/tenants"
                element={
                  <ProtectedRoute>
                    <TenantsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/customers/tenants/:tenantId/users"
                element={
                  <ProtectedRoute>
                    <TenantUsersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/knowledge/base"
                element={
                  <ProtectedRoute>
                    <KBListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/knowledge/base/new"
                element={
                  <ProtectedRoute>
                    <KBEditor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/knowledge/base/:id"
                element={
                  <ProtectedRoute>
                    <KBEditor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/insights/rag"
                element={
                  <ProtectedRoute>
                    <KBSearchPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/knowledge/review"
                element={
                  <ProtectedRoute>
                    <ExcerptReviewPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/knowledge/sources"
                element={
                  <ProtectedRoute>
                    <SourceDocumentsListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/knowledge/sources/new"
                element={
                  <ProtectedRoute>
                    <SourceDocumentUploadPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/knowledge/sources/:id"
                element={
                  <ProtectedRoute>
                    <SourceDocumentDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/knowledge/sources/:id/map"
                element={
                  <ProtectedRoute>
                    <MapReviewPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/knowledge/frameworks"
                element={
                  <ProtectedRoute>
                    <FrameworksLibraryPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/knowledge/frameworks/new"
                element={
                  <ProtectedRoute>
                    <FrameworkEditorPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/knowledge/frameworks/:id"
                element={
                  <ProtectedRoute>
                    <FrameworkEditorPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/knowledge/personae"
                element={
                  <ProtectedRoute>
                    <PersonaeListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/knowledge/personae/new"
                element={
                  <ProtectedRoute>
                    <PersonaEditor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/knowledge/personae/:id"
                element={
                  <ProtectedRoute>
                    <PersonaDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/knowledge/personae/:id/edit"
                element={
                  <ProtectedRoute>
                    <PersonaEditor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/insights/labs"
                element={
                  <ProtectedRoute>
                    <LabsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/admin/settings"
                element={
                  <ProtectedRoute>
                    <SettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/" element={<Navigate to="/admin/team/users" replace />} />
              <Route path="*" element={<Navigate to="/admin/team/users" replace />} />
            </Routes>
            {/* Global mount — renders only when session-expired state is active.
                Lives inside AuthProvider so it can read useAuth(); lives outside
                <Routes> so it overlays whatever protected route is current. */}
            <SessionExpiredModal />
          </SessionProvider>
        </AuthProvider>
      </I18nProvider>
      </PortalProvider>
    </BrowserRouter>
  );
}
